(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initProductExperience = (root) => {
    if (!root || root.dataset.productInitialized === 'true') return;
    root.dataset.productInitialized = 'true';

    const gallery = root.querySelector('[data-product-gallery]');
    const track = root.querySelector('[data-gallery-track]');
    const slides = Array.from(root.querySelectorAll('[data-gallery-slide]'));
    const thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
    const thumbTrack = root.querySelector('[data-gallery-thumbs]');
    const counter = root.querySelector('[data-gallery-current]');
    let activeIndex = 0;
    let pointerStart = null;

    const showMedia = (target, options = {}) => {
      if (!slides.length || !track) return;
      let nextIndex = typeof target === 'number'
        ? target
        : slides.findIndex((slide) => String(slide.dataset.mediaId) === String(target));
      if (nextIndex < 0) return;
      nextIndex = (nextIndex + slides.length) % slides.length;
      activeIndex = nextIndex;
      track.style.transform = 'translate3d(-' + (nextIndex * 100) + '%,0,0)';
      slides.forEach((slide, index) => slide.setAttribute('aria-hidden', index === nextIndex ? 'false' : 'true'));
      thumbs.forEach((thumb, index) => {
        const active = index === nextIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (counter) counter.textContent = String(nextIndex + 1);
      const activeThumb = thumbs[nextIndex];
      if (activeThumb && thumbTrack && thumbTrack.scrollWidth > thumbTrack.clientWidth) {
        const targetLeft = activeThumb.offsetLeft - ((thumbTrack.clientWidth - activeThumb.offsetWidth) / 2);
        thumbTrack.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: options.instant || reducedMotion.matches ? 'auto' : 'smooth'
        });
      }
    };

    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => showMedia(index)));
    root.querySelector('[data-gallery-previous]')?.addEventListener('click', () => showMedia(activeIndex - 1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => showMedia(activeIndex + 1));

    if (gallery) {
      gallery.querySelector('[data-gallery-stage]')?.addEventListener('pointerdown', (event) => {
        pointerStart = event.clientX;
      });
      gallery.querySelector('[data-gallery-stage]')?.addEventListener('pointerup', (event) => {
        if (pointerStart === null) return;
        const distance = event.clientX - pointerStart;
        pointerStart = null;
        if (Math.abs(distance) > 42) showMedia(activeIndex + (distance < 0 ? 1 : -1));
      });
      gallery.querySelector('[data-gallery-stage]')?.addEventListener('pointercancel', () => { pointerStart = null; });
    }

    const variantsNode = root.querySelector('[data-product-variants]');
    const optionNamesNode = root.querySelector('[data-product-option-names]');
    let variants = [];
    let optionNames = [];
    try { variants = variantsNode ? JSON.parse(variantsNode.textContent) : []; } catch (error) { variants = []; }
    try { optionNames = optionNamesNode ? JSON.parse(optionNamesNode.textContent) : []; } catch (error) { optionNames = []; }
    let currentVariant = variants[0] || null;
    const selectedOptions = currentVariant ? currentVariant.options.slice() : [];

    const variantTitle = root.querySelector('[data-variant-title]');
    const productPrice = root.querySelector('[data-product-price]');
    const optionGroups = Array.from(root.querySelectorAll('[data-option-index]'));

    const updateVariant = () => {
      const match = variants.find((variant) => variant.options.every((value, index) => value === selectedOptions[index]));
      if (!match) return;
      currentVariant = match;
      if (variantTitle) variantTitle.textContent = match.title;
      if (productPrice) productPrice.textContent = match.price;
      if (match.mediaId) showMedia(match.mediaId);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url.toString());
    };

    optionGroups.forEach((group) => {
      const optionIndex = Number(group.dataset.optionIndex);
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        button.addEventListener('click', () => {
          selectedOptions[optionIndex] = button.dataset.optionValue;
          group.querySelectorAll('[data-option-value]').forEach((candidate) => {
            const selected = candidate === button;
            candidate.classList.toggle('is-selected', selected);
            candidate.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });
          updateVariant();
        });
      });
    });

    const messageForCurrentVariant = () => {
      const greeting = root.dataset.messageGreeting || 'Hi Acorn Marine, I am interested in:';
      const productLabel = root.dataset.messageProduct || 'Product';
      const sizeLabel = root.dataset.messageSize || 'Size';
      const heightLabel = root.dataset.messageHeight || 'Height';
      const productTitle = root.dataset.productTitle || '';
      const details = currentVariant && currentVariant.options
        ? currentVariant.options
          .map((value, index) => {
            const optionName = optionNames[index] || '';
            if (value === 'Default Title' || optionName === 'Title') return '';
            const label = currentVariant.options.length === 1 ? heightLabel : (optionName || sizeLabel);
            return label + ': ' + value;
          })
          .filter(Boolean)
        : [];
      return [greeting, productLabel + ': ' + productTitle].concat(details).join('\n');
    };

    const buildWhatsAppUrl = (message) => {
      const configuredNumber = (root.dataset.whatsappNumber || '').replace(/\D/g, '');
      if (configuredNumber) {
        return 'https://wa.me/' + configuredNumber + '?text=' + encodeURIComponent(message);
      }

      const base = root.dataset.whatsappBase || '';
      if (!base) return '';
      try {
        const fallback = new URL(base, window.location.origin);
        const queryNumber = (fallback.searchParams.get('phone') || '').replace(/\D/g, '');
        const pathNumber = fallback.hostname.includes('wa.me')
          ? (fallback.pathname.split('/').filter(Boolean)[0] || '').replace(/\D/g, '')
          : '';
        const recoveredNumber = queryNumber || pathNumber;
        if (recoveredNumber) {
          return 'https://wa.me/' + recoveredNumber + '?text=' + encodeURIComponent(message);
        }
        fallback.searchParams.set('text', message);
        return fallback.toString();
      } catch (error) {
        return base + (base.includes('?') ? '&' : '?') + 'text=' + encodeURIComponent(message);
      }
    };

    const whatsAppLink = root.querySelector('[data-order-whatsapp]');
    whatsAppLink?.addEventListener('click', (event) => {
      const destination = buildWhatsAppUrl(messageForCurrentVariant());
      if (!destination) {
        event.preventDefault();
        return;
      }
      whatsAppLink.href = destination;
    });

    root.querySelector('[data-order-email]')?.addEventListener('click', () => {
      const email = root.dataset.contactEmail;
      if (!email) return;
      const subject = root.dataset.emailSubject || 'Pool enquiry';
      window.location.href = 'mailto:' + email + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(messageForCurrentVariant());
    });

    if (currentVariant && currentVariant.mediaId) showMedia(currentVariant.mediaId, { instant: true });
  };

  const resetFreshProductNavigation = () => {
    if (window.location.hash) return;
    const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (navigation && navigation.type !== 'navigate') return;
    const moveToTop = () => window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    moveToTop();
    window.requestAnimationFrame(() => window.requestAnimationFrame(moveToTop));
  };

  const initScrollSections = (scope = document) => {
    scope.querySelectorAll('[data-horizontal-section]').forEach((section) => {
      if (section.dataset.scrollInitialized === 'true') return;
      section.dataset.scrollInitialized = 'true';
      const track = section.querySelector('[data-horizontal-track]');
      if (!track) return;
      section.querySelector('[data-scroll-previous]')?.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth * .78, behavior: 'smooth' }));
      section.querySelector('[data-scroll-next]')?.addEventListener('click', () => track.scrollBy({ left: track.clientWidth * .78, behavior: 'smooth' }));
    });
  };

  const initAnimatedDetails = (scope = document) => {
    scope.querySelectorAll('[data-animated-details]').forEach((details) => {
      if (details.dataset.detailsInitialized === 'true') return;
      const summary = details.querySelector(':scope > summary');
      const content = details.querySelector(':scope > [data-details-content]');
      if (!summary || !content) return;
      details.dataset.detailsInitialized = 'true';
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      let animation = null;

      const clearStyles = () => {
        ['height', 'opacity', 'overflow', 'paddingTop', 'paddingBottom'].forEach((property) => {
          content.style[property] = '';
        });
        details.classList.remove('is-opening', 'is-closing');
        animation = null;
      };

      const setState = (open) => {
        details.open = open;
        summary.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        animation?.cancel();

        if (reducedMotion.matches) {
          clearStyles();
          setState(!details.open);
          return;
        }

        const opening = details.classList.contains('is-closing') || !details.open;
        if (opening) setState(true);
        details.classList.toggle('is-opening', opening);
        details.classList.toggle('is-closing', !opening);

        const computed = window.getComputedStyle(content);
        const paddingTop = computed.paddingTop;
        const paddingBottom = computed.paddingBottom;
        const currentHeight = content.getBoundingClientRect().height;
        const targetHeight = content.scrollHeight;
        content.style.overflow = 'hidden';

        const openFrames = [
          { height: '0px', opacity: 0, paddingTop: '0px', paddingBottom: '0px' },
          { height: `${targetHeight}px`, opacity: 1, paddingTop, paddingBottom }
        ];
        const closeFrames = [
          { height: `${currentHeight}px`, opacity: 1, paddingTop, paddingBottom },
          { height: '0px', opacity: 0, paddingTop: '0px', paddingBottom: '0px' }
        ];
        animation = content.animate(opening ? openFrames : closeFrames, {
          duration: opening ? 320 : 250,
          easing: opening ? 'cubic-bezier(.22,.75,.2,1)' : 'cubic-bezier(.4,0,.2,1)'
        });
        animation.onfinish = () => {
          if (!opening) setState(false);
          clearStyles();
        };
      });
    });
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-product-experience]').forEach(initProductExperience);
    initScrollSections(scope);
    initAnimatedDetails(scope);
  };

  resetFreshProductNavigation();
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
